import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { 
  fetchTemplates,
  sendTestMessage,
  syncTemplate
} from '../store/slices/templatesSlice';
import { toast } from 'react-toastify';
import './TemplateDetail.css';

const TemplateDetail = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { templates, loading, error } = useSelector(state => state.templates);
  const [template, setTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  const [isSendTestModalOpen, setIsSendTestModalOpen] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testVariableValues, setTestVariableValues] = useState([]); // For custom variable overrides
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Fetch templates on mount
  useEffect(() => {
    dispatch(fetchTemplates({})).catch(err => {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load templates');
    });
  }, [dispatch]);

  // Find template by ID
  useEffect(() => {
    if (templates && templates.length > 0) {
      const found = templates.find(t => t._id === templateId || t.id === templateId);
      if (found) {
        setTemplate(found);
      } else {
        toast.error('Template not found');
        navigate('/templates');
      }
    }
  }, [templates, templateId, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeStyle = (status) => {
    const statusConfig = {
      APPROVED: { label: 'Approved', color: '#10b981', bg: '#d1fae5' },
      PENDING: { label: 'Pending Review', color: '#f59e0b', bg: '#fef3c7' },
      REJECTED: { label: 'Rejected', color: '#ef4444', bg: '#fee2e2' },
      DRAFT: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' }
    };
    return statusConfig[status] || { label: status || 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
  };

  const getQualityBadgeStyle = (qualityScore) => {
    const qualityConfig = {
      GREEN: { label: 'High Quality', color: '#10b981', bg: '#d1fae5' },
      YELLOW: { label: 'Medium Quality', color: '#f59e0b', bg: '#fef3c7' },
      RED: { label: 'Low Quality', color: '#ef4444', bg: '#fee2e2' }
    };
    return qualityConfig[qualityScore] || null;
  };

  const replaceVariablesWithExamples = (text, examples) => {
    if (!text || !examples) return text;
    let result = text;
    examples.forEach((val, idx) => {
      const varNumber = idx + 1;
      const regex = new RegExp(`\\{\\{${varNumber}\\}\\}`, 'g');
      result = result.replace(regex, val);
    });
    return result;
  };

  // Handle send test message
  const handleSendTestClick = () => {
    setIsSendTestModalOpen(true);
    setTestPhoneNumber('');
  };

  // Handle send test message submission
  const handleSendTestMessage = async () => {
    if (!testPhoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    if (testPhoneNumber.replace(/\D/g, '').length < 10) {
      toast.error('Phone number too short. Please include country code.');
      return;
    }

    setIsSendingTest(true);
    try {
      const result = await dispatch(sendTestMessage({
        templateId: template._id,
        recipientPhone: testPhoneNumber,
        variableValues: testVariableValues  // Optional custom variable overrides
      })).unwrap();

      const messageId = result?.data?.messageId || result?.messageId;
      const successMsg = messageId 
        ? `✓ Test message sent successfully!\nMessage ID: ${messageId}`
        : '✓ Test message sent successfully!';
      
      toast.success(successMsg);
      setIsSendTestModalOpen(false);
      setTestPhoneNumber('');
      setTestVariableValues([]); // Reset custom variable overrides
    } catch (err) {
      console.error('[Send Test Message] Error:', err);
      const errorMessage = err?.message || err?.error || 'Unknown error occurred';
      toast.error(`Failed to send test message: ${errorMessage}`);
    } finally {
      setIsSendingTest(false);
    }
  };

  // Handle sync template
  const handleSyncTemplate = async () => {
    if (!template?._id && !template?.id) {
      toast.error('Template ID not found');
      return;
    }

    setIsSyncing(true);
    try {
      

      // Call Redux thunk with correct parameters
      const result = await dispatch(syncTemplate({
        id: template._id || template.id
      })).unwrap();
      
      // Extract template data from response
      const syncedTemplate = result.data || result;
      
      console.log('[Sync Template] Success:', {
        status: syncedTemplate.status,
        qualityScore: syncedTemplate.qualityScore,
        name: syncedTemplate.name
      });

      // Update local template state with synced data
      setTemplate(prevTemplate => ({
        ...prevTemplate,
        ...syncedTemplate,
        status: syncedTemplate.status,
        qualityScore: syncedTemplate.qualityScore,
        rejectionReason: syncedTemplate.rejectionReason || null,
        updatedAt: syncedTemplate.updatedAt
      }));

      const qualityLabel = syncedTemplate.qualityScore === 'GREEN' ? 'High Quality' 
        : syncedTemplate.qualityScore === 'YELLOW' ? 'Medium Quality'
        : syncedTemplate.qualityScore === 'RED' ? 'Low Quality'
        : 'Not Rated';

      toast.success(
        `✓ Template synchronized successfully!\n` +
        `Status: ${syncedTemplate.status}\n` +
        `Quality: ${qualityLabel}`
      );
    } catch (err) {
      console.error('[Sync Template] Error:', err);
      
      const errorMessage = err?.message || err?.error || 'Unknown error occurred';
      const errorDetails = err?.details ? `\n\nDetails: ${err.details}` : '';
      
      // Check for specific error messages from Meta API
      if (err?.message?.includes('Template has not been submitted')) {
        toast.error('Cannot sync: Template has not been submitted to Meta yet');
      } else if (err?.message?.includes('access token')) {
        toast.error('Access token error. Please re-authenticate.');
      } else {
        toast.error(`Sync failed: ${errorMessage}${errorDetails}`);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="template-detail-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <h3>Loading template details...</h3>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="template-detail-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Template not found</h3>
          <p>The template you're looking for doesn't exist.</p>
          <button className="btn-back" onClick={() => navigate('/templates')}>
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusBadgeStyle(template.status);
  const qualityConfig = template.qualityScore ? getQualityBadgeStyle(template.qualityScore) : null;
  const exampleBodyText = template.components?.body?.example?.body_text?.[0]
    ? replaceVariablesWithExamples(template.components.body.text, template.components.body.example.body_text[0])
    : template.components?.body?.text;

  return (
    <div className="template-detail-container">
      {/* Header with Back Button */}
      <div className="detail-header">
        <div className="header-top">
          <button 
            className="btn-back-nav"
            onClick={() => navigate('/templates')}
            title="Back to Templates"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <div className="detail-title-section">
            <h1>{template.displayName || template.name}</h1>
            <div className="detail-badges-top">
              <span className="category-badge">{template.category || 'N/A'}</span>
              <span className="language-badge">{template.language ? template.language.toUpperCase() : 'EN'}</span>
            </div>
          </div>
          <div className="detail-status-section">
            <span 
              className="status-badge" 
              style={{ background: statusConfig.bg, color: statusConfig.color }}
            >
              {statusConfig.label}
            </span>
            {qualityConfig && (
              <span 
                className="quality-badge" 
                style={{ background: qualityConfig.bg, color: qualityConfig.color }}
              >
                {qualityConfig.label}
              </span>
            )}
          </div>
        </div>

        {/* Rejection Reason (if applicable) */}
        {template.rejectionReason && (
          <div className="rejection-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <div className="rejection-content">
              <strong>Rejection Reason:</strong>
              <p>{template.rejectionReason}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          Preview
        </button>
        <button
          className={`tab-button ${activeTab === 'metadata' ? 'active' : ''}`}
          onClick={() => setActiveTab('metadata')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
          Details
        </button>
        <button
          className={`tab-button ${activeTab === 'raw' ? 'active' : ''}`}
          onClick={() => setActiveTab('raw')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6"></polyline>
            <polyline points="8 6 2 12 8 18"></polyline>
          </svg>
          JSON
        </button>
      </div>

      {/* Tab Content */}
      <div className="detail-content">
        {/* Preview Tab */}
        {activeTab === 'preview' && (
          <div className="preview-tab">
            <div className="whatsapp-preview-section">
              <h3>WhatsApp Message Preview</h3>
              <div className="whatsapp-message-preview-full">
                <div className="whatsapp-message-container-full">
                  {/* Header */}
                  {template.components?.header && (
                    <div className="whatsapp-message-header-full">
                      {template.components.header.type === 'TEXT' && (
                        <div className="header-text-full">
                          {template.components.header.text}
                        </div>
                      )}
                      {template.components.header.type === 'IMAGE' && (
                        <div className="header-media-full image-placeholder-full">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          <span>Image</span>
                        </div>
                      )}
                      {template.components.header.type === 'VIDEO' && (
                        <div className="header-media-full video-placeholder-full">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <polygon points="23 7 16 12 23 17 23 7"></polygon>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                          </svg>
                          <span>Video</span>
                        </div>
                      )}
                      {template.components.header.type === 'DOCUMENT' && (
                        <div className="header-media-full document-placeholder-full">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                          <span>Document</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Body Message */}
                  <div className="whatsapp-message-bubble-full">
                    <div className="whatsapp-message-body-full">
                      {exampleBodyText || 'No body text'}
                    </div>
                    {template.components?.footer?.text && (
                      <div className="whatsapp-message-footer-full">
                        {template.components.footer.text}
                      </div>
                    )}
                    <div className="whatsapp-message-time-full">
                      <span>Template</span>
                      <svg width="14" height="14" viewBox="0 0 16 15" fill="currentColor">
                        <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.541l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.525a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185a.32.32 0 0 0 .484-.033l6.272-8.525a.365.365 0 0 0-.063-.51z"/>
                      </svg>
                    </div>
                  </div>

                  {/* Buttons */}
                  {template.components?.buttons && template.components.buttons.length > 0 && (
                    <div className="whatsapp-message-buttons-full">
                      {template.components.buttons.map((btn, idx) => (
                        <div key={idx} className="whatsapp-button-full">
                          {btn.type === 'QUICK_REPLY' && (
                            <span className="button-text-full">{btn.text}</span>
                          )}
                          {btn.type === 'URL' && (
                            <span className="button-text-full">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                              {btn.text}
                            </span>
                          )}
                          {btn.type === 'PHONE_NUMBER' && (
                            <span className="button-text-full">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                              </svg>
                              {btn.text}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Variables Section */}
            {template.variables && template.variables.length > 0 && (
              <div className="variables-section">
                <h3>Template Variables</h3>
                <div className="variables-list-full">
                  {template.variables.map((variable, index) => (
                    <div key={index} className="variable-item-full">
                      <span className="variable-placeholder">{`{{${index + 1}}}`}</span>
                      <span className="variable-name">
                        {typeof variable === 'object'
                          ? (variable.displayText || variable.name || `Variable ${index + 1}`)
                          : (variable || `Variable ${index + 1}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metadata Tab */}
        {activeTab === 'metadata' && (
          <div className="metadata-tab">
            <div className="metadata-section">
              <h3>Basic Information</h3>
              <div className="metadata-grid">
                <div className="metadata-row">
                  <label>Template Name:</label>
                  <span className="value">{template.name}</span>
                </div>
                <div className="metadata-row">
                  <label>Display Name:</label>
                  <span className="value">{template.displayName || template.name}</span>
                </div>
                <div className="metadata-row">
                  <label>Category:</label>
                  <span className="value">{template.category || 'N/A'}</span>
                </div>
                <div className="metadata-row">
                  <label>Language:</label>
                  <span className="value">{template.language ? template.language.toUpperCase() : 'EN'}</span>
                </div>
                <div className="metadata-row">
                  <label>Status:</label>
                  <span 
                    className="value badge"
                    style={{ background: statusConfig.bg, color: statusConfig.color }}
                  >
                    {statusConfig.label}
                  </span>
                </div>
                {qualityConfig && (
                  <div className="metadata-row">
                    <label>Quality Score:</label>
                    <span 
                      className="value badge"
                      style={{ background: qualityConfig.bg, color: qualityConfig.color }}
                    >
                      {qualityConfig.label}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="metadata-section">
              <h3>System Information</h3>
              <div className="metadata-grid">
                <div className="metadata-row">
                  <label>Template ID:</label>
                  <span className="value monospace">{template._id || template.id || 'N/A'}</span>
                </div>
                {template.metaTemplateId && (
                  <div className="metadata-row">
                    <label>Meta Template ID:</label>
                    <span className="value monospace">{template.metaTemplateId}</span>
                  </div>
                )}
                {template.wabaId && (
                  <div className="metadata-row">
                    <label>WABA ID:</label>
                    <span className="value monospace">{template.wabaId}</span>
                  </div>
                )}
                {template.namespace && (
                  <div className="metadata-row">
                    <label>Namespace:</label>
                    <span className="value monospace">{template.namespace}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="metadata-section">
              <h3>Timeline</h3>
              <div className="metadata-grid">
                <div className="metadata-row">
                  <label>Created At:</label>
                  <span className="value">{formatDate(template.createdAt)}</span>
                </div>
                {template.approvedAt && (
                  <div className="metadata-row">
                    <label>Approved At:</label>
                    <span className="value">{formatDate(template.approvedAt)}</span>
                  </div>
                )}
                {template.updatedAt && (
                  <div className="metadata-row">
                    <label>Updated At:</label>
                    <span className="value">{formatDate(template.updatedAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Message Components Details */}
            <div className="metadata-section">
              <h3>Message Components</h3>
              <div className="components-grid">
                {template.components?.header && (
                  <div className="component-item">
                    <h4>Header</h4>
                    <p><strong>Type:</strong> {template.components.header.type}</p>
                    {template.components.header.type === 'TEXT' && (
                      <p><strong>Text:</strong> {template.components.header.text}</p>
                    )}
                  </div>
                )}
                {template.components?.body && (
                  <div className="component-item">
                    <h4>Body</h4>
                    <p><strong>Text:</strong></p>
                    <p className="component-text">{template.components.body.text}</p>
                  </div>
                )}
                {template.components?.footer && (
                  <div className="component-item">
                    <h4>Footer</h4>
                    <p className="component-text">{template.components.footer.text}</p>
                  </div>
                )}
                {template.components?.buttons && template.components.buttons.length > 0 && (
                  <div className="component-item">
                    <h4>Buttons ({template.components.buttons.length})</h4>
                    <ul className="buttons-list">
                      {template.components.buttons.map((btn, idx) => (
                        <li key={idx}>
                          <strong>{btn.type}:</strong> {btn.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Raw JSON Tab */}
        {activeTab === 'raw' && (
          <div className="raw-tab">
            <div className="raw-json-section">
              <h3>Template JSON</h3>
              <div className="json-viewer">
                <pre>{JSON.stringify(template, null, 2)}</pre>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="detail-footer">
        <button 
          className="btn-back-footer"
          onClick={() => navigate('/templates')}
        >
          Back to Templates
        </button>
        {template && (
          <>
            <button 
              className="btn-sync-footer"
              onClick={handleSyncTemplate}
              disabled={isSyncing}
              title="Sync with Meta WhatsApp"
            >
              {isSyncing ? (
                <>
                  <span className="spinner-small"></span>
                  Syncing...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36M20.49 15a9 9 0 0 1-14.85 3.36"></path>
                  </svg>
                  Sync
                </>
              )}
            </button>
            <button 
              className="btn-test-footer"
              onClick={handleSendTestClick}
              title="Send test message"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              Send Test
            </button>
          </>
        )}
      </div>

      {/* Send Test Message Modal */}
      {isSendTestModalOpen && (
        <div className="modal-overlay-test" onClick={() => setIsSendTestModalOpen(false)}>
          <div className="modal-content-test" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-test">
              <h2>Send Test Message</h2>
              <button 
                className="close-btn-test"
                onClick={() => setIsSendTestModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body-test">
              <label htmlFor="phone-input" className="phone-label">
                Recipient Phone Number
              </label>
              <PhoneInput
                id="phone-input"
                country={'in'}
                value={testPhoneNumber}
                onChange={setTestPhoneNumber}
                inputProps={{
                  placeholder: 'Enter phone number',
                  className: 'phone-input-field'
                }}
                containerClass="phone-input-container"
              />
              <p className="phone-help-text">
                Include the country code (e.g., +91 for India, +1 for USA, +44 for UK)
              </p>
            </div>

            <div className="modal-footer-test">
              <button
                className="btn-modal-cancel-test"
                onClick={() => setIsSendTestModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="btn-modal-send-test"
                onClick={handleSendTestMessage}
                disabled={isSendingTest || !testPhoneNumber.trim()}
              >
                {isSendingTest ? (
                  <>
                    <span className="spinner-small"></span>
                    Sending...
                  </>
                ) : (
                  'Send Test Message'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateDetail;
